import React, { useState, useEffect } from "react";
import moment from "moment";
import "../styles/db.css";
import { Row, Button, Dropdown } from "antd";
import {
  DownCircleFilled,
  SearchOutlined,
  PlusOutlined,
  ExperimentOutlined,
} from "@ant-design/icons";
import TableComponent from "../components/table/TableComponent";
import PageHeader from "../components/generic/PageHeader";
import { getAllSuiteData } from "../service/suite.service";
import { useNavigate } from "react-router-dom";
import RunTaskModal from "../components/modal/RunTaskModal";
import SearchFilter from "../components/generic/SearchFilter";
import ColumnToggle from "../components/generic/ColumnToggle";
import { useColumnToggle } from "../helper/useColumnToggle";

// ─── Optional columns — user can show/hide these ──────────────
const OPTIONAL_COLUMNS = [
  "createdBy",
  "createdDate",
  "modifiedBy",
  "modifiedDate",
  "labels",
  "comments",
  "taskCount",
];

function TestSuite() {
  const navigate = useNavigate();
  const [suiteData, setSuiteDate] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [testSuiteName, setTestSuiteName] = useState();
  const [testSuiteId, setTestSuiteId] = useState();
  const [tableLoading, setTableLoading] = useState(true);
  const [specName, setSpecName] = useState();

  const items = [
    { key: "1", label: "Run" },
    { key: "2", label: "Create Task" },
    { key: "3", label: "Create Custom Suite" },
  ];

  const openModal = () => setIsOpen(!isOpen);

  const handleDropDownMenuClick = (index, record) => {
    if (index === 1) {
      openModal();
      setSpecName(record.controlDocumentName);
      setTestSuiteName(record.testSuiteName);
      setTestSuiteId(record.testSuiteId);
    }
  };

  // ─── All columns definition ───────────────────────────────
  const allColumns = [
    {
      title: "ID",
      dataIndex: "testSuiteId",
      sorter: (a, b) => a.testSuiteId.localeCompare(b.testSuiteId),
    },
    {
      title: "Test Suite Type",
      dataIndex: "testSuiteType",
      sorter: (a, b) => a.testSuiteType.localeCompare(b.testSuiteType),
      defaultSortOrder: "ascend",
      filters: [
        { text: "Specification", value: "Specification" },
        { text: "Database", value: "Database" },
        { text: "default", value: "default" },
      ],
      filterMode: "tree",
      onFilter: (value, record) => (record.testSuiteType || "").includes(value),
    },
    {
      title: "Test Suite Name",
      dataIndex: "testSuiteName",
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <SearchFilter
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      filterIcon: () => (
        <SearchOutlined style={{ color: "rgb(255,255,255)" }} />
      ),
      onFilter: (value, record) =>
        record.testSuiteName.toLowerCase().includes(value.toLowerCase()),
      sorter: (a, b) => a.testSuiteName.localeCompare(b.testSuiteName),
    },
    {
      title: "Control Document",
      dataIndex: "controlDocumentName",
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <SearchFilter
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      filterIcon: () => (
        <SearchOutlined style={{ color: "rgb(255,255,255)" }} />
      ),
      onFilter: (value, record) =>
        record.controlDocumentName.toLowerCase().includes(value.toLowerCase()),
      sorter: (a, b) =>
        a.controlDocumentName.localeCompare(b.controlDocumentName),
    },
    // ── Optional columns below ────────────────────────────
    {
      title: "Label(s)",
      dataIndex: "labels",
      sorter: (a, b) => a.labels.localeCompare(b.labels),
    },
    {
      title: "Created By",
      dataIndex: "createdBy",
      sorter: (a, b) => a.createdBy.localeCompare(b.createdBy),
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      sorter: (a, b) =>
        moment(a.createdDate, "DD-MM-YYYY HH:mm:ss") -
        moment(b.createdDate, "DD-MM-YYYY HH:mm:ss"),
      defaultSortOrder: "descend",
    },
    {
      title: "Modified By",
      dataIndex: "modifiedBy",
      sorter: (a, b) =>
        moment(a.lastUpdated, "DD-MM-YYYY HH:mm:ss") -
        moment(b.lastUpdated, "DD-MM-YYYY HH:mm:ss"),
      defaultSortOrder: "descend",
    },
    {
      title: "Modified Date",
      dataIndex: "modifiedDate",
      sorter: (a, b) =>
        moment(a.modifiedDate, "DD-MM-YYYY HH:mm:ss") -
        moment(b.modifiedDate, "DD-MM-YYYY HH:mm:ss"),
      defaultSortOrder: "descend",
    },
    {
      title: "Comments",
      dataIndex: "comments",
      filterDropdown: ({
        selectedKeys,
        setSelectedKeys,
        confirm,
        clearFilters,
      }) => (
        <SearchFilter
          selectedKeys={selectedKeys}
          setSelectedKeys={setSelectedKeys}
          confirm={confirm}
          clearFilters={clearFilters}
        />
      ),
      filterIcon: () => (
        <SearchOutlined style={{ color: "rgb(255,255,255)" }} />
      ),
      onFilter: (value, record) =>
        record.comments.toLowerCase().includes(value.toLowerCase()),
      sorter: (a, b) => a.comments.localeCompare(b.comments),
    },
    {
      title: "Tests",
      dataIndex: "testCaseCount",
      render: (text) => <span className="table-tests-count">{text}</span>,
      onCell: (record) => ({
        style: { cursor: "pointer" },
        onClick: () => {
          navigate("/suite/details", {
            state: {
              key: record.testSuiteId,
              name: record.testSuiteName,
              specName: record.controlDocumentName,
            },
          });
        },
      }),
      sorter: (a, b) => a.testCaseCount - b.testCaseCount,
    },
    {
      title: "Tasks",
      dataIndex: "taskCount",
    },
    {
      title: "Actions",
      dataIndex: "operation",
      width: "10%",
      render: (_, record) => (
        <Dropdown
          menu={{
            items,
            onClick: (e) => handleDropDownMenuClick(e.key, record),
          }}
          overlayClassName="table-row-actions-dropdown"
        >
          <button type="button" className="table-more-options">
            Options <DownCircleFilled />
          </button>
        </Dropdown>
      ),
    },
  ];

  // ─── Column toggle hook ───────────────────────────────────
  const { visibleColumns, columnToggleProps } = useColumnToggle(
    allColumns,
    OPTIONAL_COLUMNS,
    "arc_cols_testsuite",
  );

  useEffect(() => {
    getAllSuiteData().then((res) => {
      setSuiteDate(res.data);
      setTableLoading(false);
    });
  }, []);

  return (
    <div className="db-container">
      <PageHeader
        icon={<ExperimentOutlined />}
        title="Test Suite Library"
        tooltip="Manage and run test suite"
        actions={
          <>
            <Button className="db-add">Create Label</Button>
            <Button className="db-add" icon={<PlusOutlined />}>
              New Test Suite
            </Button>
            <ColumnToggle {...columnToggleProps} />
          </>
        }
      />
      <Row className="table-container">
        <TableComponent
          loading={tableLoading}
          columns={visibleColumns}
          data={suiteData}
          pageSize={20}
        />
      </Row>
      <RunTaskModal
        isOpen={isOpen}
        openModal={openModal}
        suiteName={testSuiteName}
        specName={specName}
        suiteId={testSuiteId}
      />
    </div>
  );
}

export default TestSuite;
